import axios from 'axios';
import { AUTH_CONFIG } from './auth0-variables';

const REFRESH_BUFFER_MS = 5 * 60 * 1000; // 5 minutes before expiry
export const REQUEST_REFRESH_BUFFER_MS = 30 * 60 * 1000; // refresh proactively on requests when < 30 minutes remain
let isIdle = false;

let refreshTimeoutId = null;
let isRefreshing = false;
let failedQueue = [];
let tokenFailureHandler = null;

const processQueue = (error, token = null) => {
    failedQueue.forEach((prom) => {
        if (error) {
            prom.reject(error);
        } else {
            prom.resolve(token);
        }
    });

    failedQueue = [];
};

function scheduleTokenRefresh(expiresAtUtc) {
    clearTokenRefreshTimer();

    if (!expiresAtUtc || !localStorage.getItem('refresh_token')) {
        return;
    }

    const expiresAt = new Date(expiresAtUtc).getTime();
    if (Number.isNaN(expiresAt)) {
        return;
    }

    const timeoutMs = Math.max(expiresAt - Date.now() - REFRESH_BUFFER_MS, 0);
    if (timeoutMs <= 0) {
        triggerScheduledRefresh();
        return;
    }

    refreshTimeoutId = setTimeout(triggerScheduledRefresh, timeoutMs);
}

function triggerScheduledRefresh() {
    if (isIdle) {
        return;
    }

    refreshAccessToken().catch((error) => {
        if (tokenFailureHandler) {
            tokenFailureHandler(error);
        }
    });
}

async function performTokenRefresh() {
    const accessToken = localStorage.getItem('access_token');
    const refreshToken = localStorage.getItem('refresh_token');

    if (!accessToken || !refreshToken) {
        throw new Error('Missing token payload');
    }

    const model = {
        AccessToken: accessToken,
        RefreshToken: refreshToken
    };

    const response = await axios.post(`${AUTH_CONFIG.API_URL}/api/auth/RefreshToken`, model);
    const data = response.data;

    if (data && data.accessToken) {
        setAuthTokens({
            accessToken: data.accessToken,
            refreshToken: data.refreshToken,
            expires: data.expires
        });

        return data.accessToken;
    }

    throw new Error('Refresh failed');
}

export function clearTokenRefreshTimer() {
    if (refreshTimeoutId) {
        clearTimeout(refreshTimeoutId);
        refreshTimeoutId = null;
    }
}

export function setAuthTokens({ accessToken, refreshToken, expires }) {
    if (accessToken) {
        localStorage.setItem('access_token', accessToken);
    }
    if (refreshToken) {
        localStorage.setItem('refresh_token', refreshToken);
    }
    if (expires) {
        localStorage.setItem('expires_at', expires);
        scheduleTokenRefresh(expires);
    }
    setIdleState(false);
}

export function getAccessTokenExpiryMs() {
    const expires = localStorage.getItem('expires_at');
    if (!expires) {
        return null;
    }
    const ts = new Date(expires).getTime();
    return Number.isNaN(ts) ? null : ts;
}

export function isAccessTokenExpiringSoon(bufferMs = REFRESH_BUFFER_MS) {
    const expiry = getAccessTokenExpiryMs();
    if (!expiry) {
        return true;
    }
    return expiry - Date.now() <= bufferMs;
}

export async function refreshAccessToken() {
    const refreshToken = localStorage.getItem('refresh_token');
    if (!refreshToken) {
        if (tokenFailureHandler) {
            tokenFailureHandler(new Error('Missing refresh token'));
        }
        throw new Error('No refresh token available');
    }

    if (isIdle) {
        throw new Error('User is idle');
    }

    if (isRefreshing) {
        return new Promise((resolve, reject) => {
            failedQueue.push({ resolve, reject });
        });
    }

    isRefreshing = true;

    try {
        const newToken = await performTokenRefresh();
        processQueue(null, newToken);
        return newToken;
    } catch (error) {
        processQueue(error, null);
        throw error;
    } finally {
        isRefreshing = false;
    }
}

export function bootstrapTokenRefresh() {
    const existingExpiry = localStorage.getItem('expires_at');
    if (existingExpiry) {
        scheduleTokenRefresh(existingExpiry);
    }
}

export function setTokenFailureHandler(handler) {
    tokenFailureHandler = handler;
}

export function setIdleState(idle) {
    if (idle) {
        isIdle = true;
        clearTokenRefreshTimer();
    } else {
        const wasIdle = isIdle;
        isIdle = false;
        if (wasIdle) {
            bootstrapTokenRefresh();
        }
    }
}
